import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Slider } from '@/components/ui/slider';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Package,
  FileText,
  TestTube,
  Star,
  Upload,
  Camera,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { useProcurement } from '@/context/ProcurementContext';
import { formatCurrency, formatRating } from '@/lib/utils';

interface InvoiceQualityTestingModalProps {
  invoice: any;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (invoiceId: number, testResults: any) => Promise<void>;
  currentUser: any;
}

interface QualityTestItem {
  itemId: number;
  skuId: number;
  quantity: number;
  qualityRating: number;
  sampleSize: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  rejectionReasons: string[];
  testResults: Record<string, any>;
  notes: string;
  images: string[];
}

export const InvoiceQualityTestingModal: React.FC<InvoiceQualityTestingModalProps> = ({
  invoice,
  isOpen,
  onClose,
  onComplete,
  currentUser
}) => {
  const { state } = useProcurement();
  const [testItems, setTestItems] = useState<QualityTestItem[]>([]);
  const [overallRating, setOverallRating] = useState<number>(5);
  const [overallNotes, setOverallNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);

  useEffect(() => {
    if (invoice && isOpen) {
      // Initialize test items from invoice
      const items = invoice.items.map((item: any, index: number) => ({
        itemId: item.id,
        skuId: item.skuId,
        quantity: item.quantity,
        qualityRating: 5,
        sampleSize: Math.min(Math.ceil(item.quantity * 0.1), 10), // 10% sample, max 10 items
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        rejectionReasons: [],
        testResults: {},
        notes: '',
        images: []
      }));
      setTestItems(items);
      setOverallRating(5);
      setOverallNotes('');
      setUploadedImages([]);
    }
  }, [invoice, isOpen]);

  const handleRatingChange = (itemIndex: number, rating: number) => {
    const updatedItems = [...testItems];
    updatedItems[itemIndex].qualityRating = rating;
    
    // Auto-calculate accepted/rejected quantities based on rating
    const item = updatedItems[itemIndex];
    if (rating >= 5) {
      // Fully accepted
      item.acceptedQuantity = item.quantity;
      item.rejectedQuantity = 0;
    } else if (rating >= 3) {
      // Partly accepted - calculate based on rating
      const acceptanceRate = rating / 5;
      item.acceptedQuantity = Math.floor(item.quantity * acceptanceRate);
      item.rejectedQuantity = item.quantity - item.acceptedQuantity;
    } else {
      // Rejected
      item.acceptedQuantity = 0;
      item.rejectedQuantity = item.quantity;
    }
    
    setTestItems(updatedItems);
    
    // Update overall rating
    const avgRating = updatedItems.reduce((sum, item) => sum + item.qualityRating, 0) / updatedItems.length;
    setOverallRating(parseFloat(avgRating.toFixed(1)));
  };

  const handleSampleSizeChange = (itemIndex: number, sampleSize: number) => {
    const updatedItems = [...testItems];
    updatedItems[itemIndex].sampleSize = Math.min(sampleSize, updatedItems[itemIndex].quantity);
    setTestItems(updatedItems);
  };

  const handleRejectionReasonAdd = (itemIndex: number, reason: string) => {
    if (reason.trim()) {
      const updatedItems = [...testItems];
      updatedItems[itemIndex].rejectionReasons.push(reason.trim());
      setTestItems(updatedItems);
    }
  };

  const handleRejectionReasonRemove = (itemIndex: number, reasonIndex: number) => {
    const updatedItems = [...testItems];
    updatedItems[itemIndex].rejectionReasons.splice(reasonIndex, 1);
    setTestItems(updatedItems);
  };

  const handleImageUpload = (itemIndex: number, files: FileList) => {
    // In a real app, you would upload to a server
    // For now, we'll just simulate with file names
    const newImages = Array.from(files).map(file => file.name);
    const updatedItems = [...testItems];
    updatedItems[itemIndex].images = [...updatedItems[itemIndex].images, ...newImages];
    setTestItems(updatedItems);
    toast.success(`${newImages.length} image(s) uploaded`);
  };

  const handleOverallImageUpload = (files: FileList) => {
    const newImages = Array.from(files).map(file => file.name);
    setUploadedImages([...uploadedImages, ...newImages]);
    toast.success(`${newImages.length} image(s) uploaded`);
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 5) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingLabel = (rating: number) => {
    if (rating >= 5) return 'Excellent';
    if (rating >= 4) return 'Good';
    if (rating >= 3) return 'Acceptable';
    if (rating >= 2) return 'Poor';
    return 'Very Poor';
  };

  const getOverallStatus = (rating: number) => {
    if (rating >= 5) return 'Fully Accepted';
    if (rating >= 3) return 'Partly Accepted';
    return 'Rejected';
  };

  const handleSubmit = async () => {
    if (testItems.some(item => item.qualityRating < 3 && item.notes.trim() === '')) {
      toast.error('Please add notes for items with rating below 3');
      return;
    }

    if (testItems.some(item => item.qualityRating < 3 && item.images.length === 0)) {
      toast.error('Please upload images for items with rating below 3');
      return;
    }

    setIsSubmitting(true);
    try {
      const testResults = {
        testItems,
        overallRating,
        overallNotes,
        overallImages: uploadedImages,
        overallStatus: getOverallStatus(overallRating),
        testedBy: currentUser?.id || 1,
        testedAt: new Date().toISOString()
      };

      await onComplete(invoice.id, testResults);
      
      // Show notification based on result
      if (overallRating >= 5) {
        toast.success('Quality testing completed - Invoice fully accepted');
      } else if (overallRating >= 3) {
        toast.warning('Quality testing completed - Invoice partly accepted. Vendor and managers will be notified.');
      } else {
        toast.error('Quality testing completed - Invoice rejected. Vendor and managers will be notified.');
      }
      
    } catch (error) {
      console.error('Failed to complete quality testing:', error);
      toast.error('Failed to complete quality testing');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!invoice) return null;

  const totalQuantity = testItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5" />
            Quality Testing - {invoice.invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            Perform quality testing on sample products from this invoice
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Invoice Number</p>
                  <p className="font-medium">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">PO Number</p>
                  <p className="font-medium">{invoice.poNumber}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Vendor</p>
                  <p className="font-medium">{invoice.vendorName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Amount</p>
                  <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quality Testing Items */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quality Testing Items</h3>
            {testItems.map((item, index) => {
              const sku = state.skus.find(s => s.id === item.skuId);
              return (
                <Card key={item.itemId}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {sku?.name || `SKU ${item.skuId}`}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Quantity</p>
                        <p className="font-medium">{item.quantity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Sample Size</p>
                        <Input
                          type="number"
                          min="1"
                          max={item.quantity}
                          value={item.sampleSize}
                          onChange={(e) => handleSampleSizeChange(index, parseInt(e.target.value) || 1)}
                          className="w-20"
                        />
                      </div>
                    </div>

                    {/* Quality Rating */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Quality Rating: {formatRating(item.qualityRating)}/5 - {getRatingLabel(item.qualityRating)}
                      </Label>
                      <div className="flex items-center gap-4">
                        <Slider
                          value={[item.qualityRating]}
                          onValueChange={([value]) => handleRatingChange(index, value)}
                          max={5}
                          min={1}
                          step={0.5}
                          className="flex-1"
                        />
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-5 w-5 ${
                                star <= item.qualityRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Rejection Reasons (for rating < 3) */}
                    {item.qualityRating < 3 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-red-600">Rejection Reasons *</Label>
                        <div className="space-y-2">
                          {item.rejectionReasons.map((reason, reasonIndex) => (
                            <div key={reasonIndex} className="flex items-center gap-2">
                              <span className="text-sm">{reason}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectionReasonRemove(index, reasonIndex)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add rejection reason..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleRejectionReasonAdd(index, e.currentTarget.value);
                                  e.currentTarget.value = '';
                                }
                              }}
                              className="flex-1"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                handleRejectionReasonAdd(index, input.value);
                                input.value = '';
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Upload (for rating < 3) */}
                    {item.qualityRating < 3 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-red-600">Upload Images *</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => e.target.files && handleImageUpload(index, e.target.files)}
                            className="flex-1"
                          />
                          <Camera className="h-4 w-4 text-muted-foreground" />
                        </div>
                        {item.images.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {item.images.map((image, imgIndex) => (
                              <Badge key={imgIndex} variant="secondary" className="text-xs">
                                {image}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Notes</Label>
                      <Textarea
                        placeholder="Add any additional notes about this item..."
                        value={item.notes}
                        onChange={(e) => {
                          const updatedItems = [...testItems];
                          updatedItems[index].notes = e.target.value;
                          setTestItems(updatedItems);
                        }}
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Overall Assessment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Overall Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Overall Rating</p>
                  <p className={`font-medium text-lg ${getRatingColor(overallRating)}`}>
                    {formatRating(overallRating)}/5 - {getRatingLabel(overallRating)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge variant={overallRating >= 5 ? 'default' : overallRating >= 3 ? 'secondary' : 'destructive'}>
                    {getOverallStatus(overallRating)}
                  </Badge>
                </div>
              </div>

              {/* Overall Notes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Overall Notes</Label>
                <Textarea
                  placeholder="Add overall assessment notes..."
                  value={overallNotes}
                  onChange={(e) => setOverallNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Overall Image Upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Upload Overall Images</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => e.target.files && handleOverallImageUpload(e.target.files)}
                    className="flex-1"
                  />
                  <Camera className="h-4 w-4 text-muted-foreground" />
                </div>
                {uploadedImages.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((image, imgIndex) => (
                      <Badge key={imgIndex} variant="secondary" className="text-xs">
                        {image}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notifications Alert */}
          {(overallRating < 5) && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {overallRating >= 3 
                  ? 'This invoice will be marked as partly accepted. The vendor and managers will be notified of the quality issues.'
                  : 'This invoice will be rejected. The vendor and managers will be notified immediately.'
                }
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Complete Quality Testing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

